import { ErrorHandler } from '@src/components/ErrorHandler';
import { ListCountries, ListTerritoryProvinces } from '@src/includes/functions';
import { fieldValidator } from '@src/includes/validation';
import { ToolTipStyles } from '@src/routes/private/MemberProfile/components/Modals/includes/styles';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';

import fields from '../../../../includes/fields.json';

/**
 * Tab: InternationalAddress
 *
 * Displays international address information about a member's policy.
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
export function InternationalAddress(props) {
  const { form, isInternational, voidFormDisabled } = props;

  const city = form.watch('ui[internationalAddress][city]');
  const countryCode = form.watch('ui[internationalAddress][countryCode]');
  const postalCode = form.watch('ui[internationalAddress][postalCode]');
  const stateOrRegion = form.watch('ui[internationalAddress][stateOrRegion]');
  const streetAddress = form.watch('ui[internationalAddress][streetAddress]');
  const suite = form.watch('ui[internationalAddress][suite]');

  /**
   * ListCountries
   *
   * Retrieves a list of countries.
   *
   * @type {{code: *, name: *}[]}
   */
  const Countries = useMemo(() => {
    return ListCountries().filter((country) => {
      return !(isInternational && country?.code === 'US');
    });
  }, [isInternational]);

  /**
   * TerritoriesProvinces
   *
   * Retrieves a list of states.
   *
   * @type {{code: *, name: *}[]}
   */
  const TerritoriesProvinces = useMemo(() => {
    return ListTerritoryProvinces(countryCode);
  }, [countryCode]);

  useEffect(() => {
    if (!isEmpty(streetAddress)) {
      form.validate(
        'ui[internationalAddress][streetAddress]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(suite)) {
      form.validate(
        'ui[internationalAddress][suite]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(postalCode)) {
      form.validate(
        'ui[internationalAddress][postalCode]',
        () => { },
        () => { }
      );
    }
  }, [streetAddress, suite, postalCode]);

  return (
    <ErrorHandler location="src/routes/private/MemberProfile/components/Modals/CondfComm/components/Address/components/InternationalAddress/InternationalAddress.jsx">
      <Grid className="section first">
        <Box className="box">
          <Heading offset={5}>Address Line 1:</Heading>
        </Box>
        <Grid.Col span={{ lg: 4, xl: 4, xs: 12 }}>
          <TextInput
            {...fields?.internationalAddress?.streetAddress}
            isDisabled={voidFormDisabled}
            validators={{
              ...fields?.internationalAddress?.streetAddress?.validators,
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fields?.internationalAddress?.streetAddress, value);
                },
              },
            }}
          />
        </Grid.Col>
      </Grid>
      <Grid className="section">
        <Box className="box">
          <Heading offset={5}>Address Line 2:</Heading>
        </Box>
        <Grid.Col span={{ lg: 4, xl: 4, xs: 12 }}>
          <TextInput
            {...fields?.internationalAddress?.suite}
            isDisabled={voidFormDisabled}
            validators={{
              ...fields?.internationalAddress?.suite?.validators,
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fields?.internationalAddress?.suite, value);
                },
              },
            }}
          />
        </Grid.Col>
      </Grid>
      <Grid className="section">
        <Box className="box">
          <Heading offset={5}>Address Line 3:</Heading>
        </Box>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <SelectInput
            {...fields?.internationalAddress?.countryCode}
            isDisabled={!isInternational || voidFormDisabled}
            onChange={() => { }}
            onClear={() => { }}
            options={Countries.map((country) => {
              return {
                label: country?.name,
                value: country?.code,
              };
            })}
          />
        </Grid.Col>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <SelectInput
            {...fields?.internationalAddress?.stateOrRegion}
            isDisabled={voidFormDisabled || isEmpty(countryCode)}
            options={TerritoriesProvinces.map((territoryProvince) => {
              return {
                label: territoryProvince?.name,
                value: territoryProvince?.code,
              };
            })}
          />
        </Grid.Col>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <TextInput
            {...fields?.internationalAddress?.city}
            isDisabled={voidFormDisabled || isEmpty(countryCode) || isEmpty(stateOrRegion)}
          />
        </Grid.Col>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <TextInput
            {...fields?.internationalAddress?.postalCode}
            formatter={(value) => {
              return value.replace(/[-`&/\\#,+()$~%.'":*?<>{}@!^_=;\][|]/g, '');
            }}
            isDisabled={voidFormDisabled || isEmpty(countryCode) || isEmpty(stateOrRegion) || isEmpty(city)}
            label={
              <ToolTipStyles>
                <Tooltip asChild content="will allow zip + 4 or international zip code">
                  <strong>{fields?.internationalAddress?.postalCode?.label}</strong>
                </Tooltip>
              </ToolTipStyles>
            }
            validators={{
              ...fields?.internationalAddress?.postalCode?.validators,
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fields?.internationalAddress?.postalCode, value);
                },
              },
            }}
          />
        </Grid.Col>
      </Grid>
    </ErrorHandler>
  );
}

InternationalAddress.propTypes = {
  form: PropTypes.shape({
    getValues: PropTypes.func,
    setValue: PropTypes.func,
  }),
  TerritoriesProvinces: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  voidFormDisabled: PropTypes.bool,
};

InternationalAddress.defaultProps = {
  form: {},
  TerritoriesProvinces: [],
  voidFormDisabled: false,
};
